import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "@/api/auth";
import { consumeSessionHandoff } from "@/utils/sessionHandoff";

const AuthContext = createContext(null);

const ORIG_TOKEN_KEY = "impersonation_original_token";
const ORIG_USER_KEY  = "impersonation_original_user";

// Runs once at module load, before AuthProvider mounts or any request reads the
// token: if we arrived here via a cross-subdomain session handoff, the tokens
// are moved from the URL fragment into this origin's localStorage now, so the
// normal bootstrap below finds them and no re-login is needed.
consumeSessionHandoff();

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [isLoading, setIsLoading]   = useState(true);
  const [impersonating, setImpersonating] = useState(
    () => !!localStorage.getItem(ORIG_TOKEN_KEY)
  );

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) { setIsLoading(false); return; }
    try {
      const me = await authApi.me();
      setUser(me);
    } catch (err) {
      // Only clear tokens on confirmed auth failure (401).
      // Network errors and 5xx (e.g. server restart) must NOT clear tokens —
      // the session is still valid and the interceptor will handle the refresh
      // flow automatically. Clearing tokens on a transient 503 would force the
      // user to re-enter credentials after a brief backend downtime.
      // Note: 401s from /api/auth/me are normally caught by the axios interceptor
      // (which retries after refresh), so they should rarely reach here.
      if (err?.response?.status === 401) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(ORIG_TOKEN_KEY);
    localStorage.removeItem(ORIG_USER_KEY);
    setUser(null);
    setImpersonating(false);
  };

  /**
   * Swap to target user session without touching their credentials.
   * Saves the admin's original token so we can restore it later.
   */
  const startImpersonation = async (targetUserId) => {
    // Save current admin session
    const originalToken = localStorage.getItem("access_token");
    localStorage.setItem(ORIG_TOKEN_KEY, originalToken);
    localStorage.setItem(ORIG_USER_KEY, JSON.stringify(user));

    // Get impersonation token from backend
    const data = await authApi.impersonate(targetUserId);
    localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    setImpersonating(true);
    return data.user;
  };

  /**
   * Restore the original admin session.
   */
  const exitImpersonation = () => {
    const originalToken = localStorage.getItem(ORIG_TOKEN_KEY);
    const originalUser  = JSON.parse(localStorage.getItem(ORIG_USER_KEY) || "null");
    if (originalToken) {
      localStorage.setItem("access_token", originalToken);
    }
    localStorage.removeItem(ORIG_TOKEN_KEY);
    localStorage.removeItem(ORIG_USER_KEY);
    setUser(originalUser);
    setImpersonating(false);
  };

  return (
    <AuthContext.Provider value={{
      user, isLoading, login, logout,
      reload: loadUser,
      impersonating,
      startImpersonation,
      exitImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

